import { Card, Stack, Typography } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useCallback, useMemo, useRef } from "react";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill.tsx";
import type { Tile } from "../../../../api/types/dashboardsTypes.ts";
import { useDashboardContext } from "../contexts/DashboardContext";
import { fadeInScale } from "../widgets/filter/settings/fadeInScale";
import TileHeaderMenu from "./TileHeaderMenu.tsx";

interface Props {
  tile: Tile;
  hover: boolean | undefined;
  showMaximizeButton?: boolean;
  menuPlacement: "inline" | "overlay";
  onOpenFullscreen?: () => void;
  shouldShowMaximize?: (widgetType: string) => boolean;
  shouldShowEdit?: (widgetType: string) => boolean;
}

const TileHeader = ({
  tile,
  hover,
  showMaximizeButton,
  menuPlacement,
  onOpenFullscreen,
  shouldShowMaximize,
  shouldShowEdit,
}: Props) => {
  const { isEditMode, deleteTile } = useDashboardContext();
  const showTitle = menuPlacement === "inline" || tile.widget.hideTitle !== true;

  const handleDeleteTile = useCallback(() => {
    if (tile) {
      deleteTile(tile.tileId);
    }
  }, [deleteTile, tile]);

  const containerRef = useRef<HTMLDivElement>(null);

  const canMaximize = useMemo(() => {
    if (showMaximizeButton) {
      if (shouldShowMaximize) {
        return shouldShowMaximize(tile.widget.type);
      }
    }
    return false;
  }, [shouldShowMaximize, showMaximizeButton, tile.widget.type]);

  const canEdit = useMemo(() => {
    if (isEditMode) {
      if (shouldShowEdit) {
        return shouldShowEdit(tile.widget.type);
      }
    }
    return false;
  }, [shouldShowEdit, isEditMode, tile.widget.type]);

  const menu = useMemo(
    () =>
      menuPlacement === "overlay" ? (
        <TileOverlayMenu hover={hover === true}>
          <TileHeaderMenu
            showMaximizeButton={canMaximize}
            showEdit={canEdit}
            showDelete={isEditMode}
            tile={tile}
            onOpenFullscreen={onOpenFullscreen}
            onDeleteTile={handleDeleteTile}
          />
        </TileOverlayMenu>
      ) : (
        <TileHeaderMenu
          showMaximizeButton={canMaximize}
          showEdit={canEdit}
          showDelete={isEditMode}
          tile={tile}
          onOpenFullscreen={onOpenFullscreen}
          onDeleteTile={handleDeleteTile}
        />
      ),
    [menuPlacement, hover, canMaximize, canEdit, isEditMode, tile, onOpenFullscreen, handleDeleteTile]
  );

  if (!tile?.widget) {
    return null;
  }

  return (
    <Stack
      ref={containerRef}
      direction="row"
      useFlexGap
      spacing={0.5}
      sx={{
        alignItems: "center",
        overflowY: "hidden",
        overflowX: "auto",
        width: "100%",
        minHeight: "fit-content",
      }}
    >
      {showTitle && (
        <Typography variant="h6" noWrap color="textPrimary" sx={{ lineHeight: "100%" }}>
          {tile.widget.nameUserInput}
        </Typography>
      )}
      <HorizontalFill />
      {menu}
    </Stack>
  );
};

const TileOverlayMenu = ({ hover, children }: PropsWithChildren<{ hover: boolean }>) => {
  return (
    <Card
      sx={{
        position: "absolute",
        right: 8,
        top: -12,
        display: hover ? "block" : "none",
        animation: `${fadeInScale} 0.2s ease-in-out`,
        boxShadow: "0px 4px 4px 0px rgba(100, 115, 129, 0.25)",
        border: "1px solid",
        borderColor: "divider",
        zIndex: 1,
      }}
      elevation={3}
    >
      {children}
    </Card>
  );
};

export default TileHeader;
